import React from 'react';
import { BannerElement, ElementStyles, DataFeed } from '../types';
import { Trash2, Type } from 'lucide-react';
import { useFontManager } from '../hooks/useFontManager';

interface PropertiesPanelProps {
  element: BannerElement | null;
  dataFeed: DataFeed | null;
  onUpdateElement: (id: string, updates: Partial<BannerElement>) => void;
  onDeleteElement: (id: string) => void;
  onOpenFontManager?: () => void;
}

export const PropertiesPanel: React.FC<PropertiesPanelProps> = ({
  element,
  dataFeed,
  onUpdateElement,
  onDeleteElement,
  onOpenFontManager
}) => {
  const { getAvailableFonts, getFontDisplayName } = useFontManager();

  if (!element) {
    return (
      <div className="w-80 bg-white border-l border-gray-200 p-4">
        <div className="text-center text-gray-500 mt-8">
          <div className="text-sm">Select an element to edit its properties</div>
        </div>
      </div>
    );
  }

  const updateStyles = (styleUpdates: Partial<ElementStyles>) => {
    onUpdateElement(element.id, {
      styles: {
        ...element.styles,
        ...styleUpdates
      }
    });
  };

  const updateProperty = (property: keyof BannerElement, value: any) => {
    onUpdateElement(element.id, { [property]: value });
  };

  const getDataFeedKeys = () => {
    if (!dataFeed) return [];
    return Object.keys(dataFeed).filter(key => key !== 'length');
  };

  const availableFonts = getAvailableFonts();

  return (
    <div className="w-80 bg-white border-l border-gray-200 h-full overflow-y-auto">
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Properties</h2>
          <button
            onClick={() => onDeleteElement(element.id)}
            className="p-2 text-red-500 hover:bg-red-50 rounded"
            title="Delete Element"
          >
            <Trash2 size={16} />
          </button>
        </div>

        <div className="space-y-4">
          {/* Position & Size */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Position & Size</h3>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-600 mb-1">X</label>
                <input
                  type="number"
                  value={element.x}
                  onChange={(e) => updateProperty('x', parseInt(e.target.value))}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Y</label>
                <input
                  type="number"
                  value={element.y}
                  onChange={(e) => updateProperty('y', parseInt(e.target.value))}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Width</label>
                <input
                  type="number"
                  value={element.width}
                  onChange={(e) => updateProperty('width', parseInt(e.target.value))}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Height</label>
                <input
                  type="number"
                  value={element.height}
                  onChange={(e) => updateProperty('height', parseInt(e.target.value))}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Content */}
          {(element.type === 'text' || element.type === 'button') && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Content</h3>
              <textarea
                value={element.content || ''}
                onChange={(e) => updateProperty('content', e.target.value)}
                placeholder="Enter text content or use {{field_name}} for data binding"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                rows={3}
              />
              
              {/* Data Binding Dropdown */}
              <div className="mt-2">
                <label className="block text-xs text-gray-600 mb-1">Data Binding Field</label>
                <select
                  value={element.dataBinding || ''}
                  onChange={(e) => updateProperty('dataBinding', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">Select a field...</option>
                  {getDataFeedKeys().map(key => (
                    <option key={key} value={key}>
                      {key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
                {element.dataBinding && (
                  <div className="mt-1 text-xs text-gray-500">
                    Current value: {dataFeed?.[element.dataBinding] ? String(dataFeed[element.dataBinding]).substring(0, 30) + (String(dataFeed[element.dataBinding]).length > 30 ? '...' : '') : 'No data'}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Image URL and Data Binding */}
          {element.type === 'image' && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Image Source</h3>
              
              {/* Data Binding Dropdown for Images */}
              <div className="mb-3">
                <label className="block text-xs text-gray-600 mb-1">Data Binding Field</label>
                <select
                  value={element.dataBinding || ''}
                  onChange={(e) => updateProperty('dataBinding', e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">Select a field...</option>
                  {getDataFeedKeys().map(key => (
                    <option key={key} value={key}>
                      {key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
                {element.dataBinding && (
                  <div className="mt-1 text-xs text-gray-500">
                    Current value: {dataFeed?.[element.dataBinding] ? String(dataFeed[element.dataBinding]).substring(0, 40) + (String(dataFeed[element.dataBinding]).length > 40 ? '...' : '') : 'No data'}
                  </div>
                )}
              </div>

              {/* Manual Image URL Input */}
              <div>
                <label className="block text-xs text-gray-600 mb-1">Or enter Image URL manually</label>
                <input
                  type="text"
                  value={element.imageUrl || ''}
                  onChange={(e) => updateProperty('imageUrl', e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Image Preview */}
              {(element.dataBinding && dataFeed?.[element.dataBinding]) || element.imageUrl ? (
                <div className="mt-2">
                  <label className="block text-xs text-gray-600 mb-1">Preview</label>
                  <div className="w-full h-20 bg-gray-100 rounded border overflow-hidden">
                    <img
                      src={element.dataBinding && dataFeed?.[element.dataBinding] 
                        ? String(dataFeed[element.dataBinding]) 
                        : element.imageUrl || ''}
                      alt="Preview"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {/* Typography */}
          {(element.type === 'text' || element.type === 'button') && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-700">Typography</h3>
                {onOpenFontManager && (
                  <button
                    onClick={onOpenFontManager}
                    className="flex items-center space-x-1 text-xs text-purple-600 hover:text-purple-800 transition-colors"
                    title="Manage Custom Fonts"
                  >
                    <Type size={12} />
                    <span>Fonts</span>
                  </button>
                )}
              </div>
              
              <div className="space-y-2">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Font Family</label>
                  <select
                    value={element.styles.fontFamily || 'Arial, sans-serif'}
                    onChange={(e) => updateStyles({ fontFamily: e.target.value })}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {availableFonts.map(font => (
                      <option key={font} value={font}>
                        {getFontDisplayName(font)}
                      </option>
                    ))}
                  </select>
                  {element.styles.fontFamily && element.styles.fontFamily.startsWith('custom-') && (
                    <div className="mt-1 text-xs text-purple-600">
                      ✓ Custom font applied
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Font Size</label>
                  <input
                    type="range"
                    min="8"
                    max="72"
                    value={element.styles.fontSize || 16}
                    onChange={(e) => updateStyles({ fontSize: parseInt(e.target.value) })}
                    className="w-full"
                  />
                  <div className="text-xs text-gray-500 text-center">{element.styles.fontSize || 16}px</div>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Font Weight</label>
                  <select
                    value={element.styles.fontWeight || '400'}
                    onChange={(e) => updateStyles({ fontWeight: e.target.value })}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="300">Light</option>
                    <option value="400">Normal</option>
                    <option value="500">Medium</option>
                    <option value="600">Semi Bold</option>
                    <option value="700">Bold</option>
                    <option value="800">Extra Bold</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Text Align</label>
                  <select
                    value={element.styles.textAlign || 'left'}
                    onChange={(e) => updateStyles({ textAlign: e.target.value as 'left' | 'center' | 'right' })}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Colors */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Colors</h3>
            <div className="space-y-2">
              {(element.type === 'text' || element.type === 'button') && (
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Text Color</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={element.styles.color || '#000000'}
                      onChange={(e) => updateStyles({ color: e.target.value })}
                      className="w-8 h-8 border border-gray-300 rounded"
                    />
                    <input
                      type="text"
                      value={element.styles.color || '#000000'}
                      onChange={(e) => updateStyles({ color: e.target.value })}
                      className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}
              <div>
                <label className="block text-xs text-gray-600 mb-1">Background Color</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={element.styles.backgroundColor || '#transparent'}
                    onChange={(e) => updateStyles({ backgroundColor: e.target.value })}
                    className="w-8 h-8 border border-gray-300 rounded"
                  />
                  <input
                    type="text"
                    value={element.styles.backgroundColor || 'transparent'}
                    onChange={(e) => updateStyles({ backgroundColor: e.target.value })}
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Spacing & Border */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Spacing & Border</h3>
            <div className="space-y-2">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Border Radius</label>
                <input
                  type="range"
                  min="0"
                  max="50"
                  value={element.styles.borderRadius || 0}
                  onChange={(e) => updateStyles({ borderRadius: parseInt(e.target.value) })}
                  className="w-full"
                />
                <div className="text-xs text-gray-500 text-center">{element.styles.borderRadius || 0}px</div>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Padding</label>
                <input
                  type="range"
                  min="0"
                  max="50"
                  value={element.styles.padding || 0}
                  onChange={(e) => updateStyles({ padding: parseInt(e.target.value) })}
                  className="w-full"
                />
                <div className="text-xs text-gray-500 text-center">{element.styles.padding || 0}px</div>
              </div>
            </div>
          </div>

          {/* Effects */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Effects</h3>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Opacity</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={element.styles.opacity || 1}
                onChange={(e) => updateStyles({ opacity: parseFloat(e.target.value) })}
                className="w-full"
              />
              <div className="text-xs text-gray-500 text-center">{Math.round((element.styles.opacity || 1) * 100)}%</div>
            </div>
          </div>

          {/* Layer */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Layer</h3>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Z-Index</label>
              <input
                type="number"
                value={element.zIndex}
                onChange={(e) => updateProperty('zIndex', parseInt(e.target.value))}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};